import "./InvestmentFlow.scss";

import { Box } from "@material-ui/core";
import { ApiErrors } from "common/backend/api-errors/errors";
import { CreateWalletInput } from "common/backend/generated/create-wallet-input";
import { File } from "common/backend/generated/file-model";
import { decrypt, encrypt } from "common/backend/types/encryption";
import { isUserFitForSale } from "common/backend/types/user-scoring";
import { AssetTypes } from "common/backend/types/utils";
import { Flow } from "common/components";
import { StepDescriptor } from "common/components/Flow";
import { SubformProps } from "common/components/SubformProps";
import {
  CreateInvestment,
  GetCurrentUserInvestments,
  LoginUser,
  UpdateUser,
} from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { STORE_CURRENT_CREATED_INVESTMENT_ACTION } from "common/store/investment/actions";
import {
  CreateInvestmentManageable,
  InvestmentManageable,
} from "common/store/investment/types";
import { SaleManageable } from "common/store/sale/types";
import { StoreState } from "common/store/types";
import {
  LoginUserManageable,
  UpdateUserManageable,
  UserManageable,
} from "common/store/user/types";
import { isEmptyObject } from "common/utils/utility";
import { errorMessages } from "common/utils/validation";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import { ethers } from "ethers";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  UserExperienceAdequacyMessage,
  UserExperienceAdequacyMessageSubform,
  UserExperienceSubform,
} from "shared/subforms";

import {
  ChooseAmountSubform,
  CostCalculationSubform,
  GoingBackCheckSubform,
  PaymentTypeSelectionSubform,
  SummarySubform,
  WalletSubform,
  WalletSuccessfullyCreatedSubform,
} from "./subforms/index";

enum DataType {
  User = "user",
  Investment = "investment",
  None = "none",
}

enum Step {
  ChooseAmount = 0,
  ConfirmExperience,
  ExperienceAdequacyMessage,
  GoingBackCheck,
  CostCalculation,
  Wallet,
  WalletSuccessfullyCreated,
  PaymentTypeSelection,
  Summary,
  Count,
}

type ValuesType = {
  [DataType.User]: UpdateUserManageable;
  [DataType.Investment]: CreateInvestmentManageable;
};

const setFieldWrapper = (
  id: string,
  value: any,
  values: ValuesType,
  dataType: DataType,
  setFieldValue: (id: string, value: any) => void
) => {
  let subvalues = { ...values[dataType] };
  subvalues[id] = value;

  switch (dataType) {
    case DataType.User:
      subvalues = new UpdateUserManageable(subvalues);
      break;
    case DataType.Investment:
      subvalues = new CreateInvestmentManageable(subvalues);
      break;
    default:
      break;
  }

  setFieldValue(
    dataType,
    subvalues
  );
};

export const InvestmentFlow = () => {
  // State
  const [error, setError] = useState("");
  const [step, setStep] = useState<number>(0);

  // Hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);
  const sale = useSelector<StoreState, SaleManageable | undefined>((state: StoreState) => {
    return Object.values(AssetTypes).reduce<SaleManageable | undefined>(
      (
        result: SaleManageable | undefined, assetType: AssetTypes
      ) => {
        let assetTypesSales = state.sales.get(assetType);
        if (!!result || !assetTypesSales) {
          return result;
        }

        let sale = assetTypesSales.find((sale) => sale.id === id);
        if (sale) {
          return sale;
        }

        return undefined;
      },
      undefined
    );
  }) || new SaleManageable(null);
  const investments = useSelector<StoreState, Array<InvestmentManageable>>((state) => state.investments);
  const currentInvestmentAmount = useMemo(
    () =>
      investments
        .filter((investment: InvestmentManageable) =>
          sale && investment.saleId === sale.id)
        .reduce(
          (
            totalAmount: number, investment: InvestmentManageable
          ) =>
            totalAmount + investment.amountShares,
          0
        ) || 0,
    [investments]
  );

  const {
    execute: createInvestment, loading: loadingCreateInvestment
  } =
    useCustomQuery(CreateInvestment({
      onError: (error: ApiErrors) => {
        switch (error) {
          case ApiErrors.INVEST_AMOUNT_EXCEEDS_SHARES:
          case ApiErrors.INVEST_AMOUNT_EXCEEDS_MAXIMAL:
            setError(t("Maximum amount of shares exceeded"));
            setStep(Step.ChooseAmount);
            break;
          case ApiErrors.INVEST_AMOUNT_LOWER_MINIMAL:
            setError(t("Amount of shares lower than minimum"));
            setStep(Step.ChooseAmount);
            break;
          default:
            break;
        }
      },
      onSuccess: () => {
        history.replace("/congratulations");
      },
    }));

  const {
    execute: updateUser, loading: loadingUpdateUser
  } = useCustomQuery(UpdateUser({}));

  const {
    execute: authenticate, loading: loadingAuthenticate
  } =
    useCustomQuery(LoginUser({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_INVALID_CREDENTIALS) {
          setError(t("Wrong password"));
        }
      },
    }));

  const storeCurrentInvestment = (values: any) => {
    dispatch({
      type: STORE_CURRENT_CREATED_INVESTMENT_ACTION,
      payload: new CreateInvestmentManageable(values),
    });
  };

  const validateInvestment = (
    keys: Array<string>, values: any
  ) => {
    return new CreateInvestmentManageable(values).validate(
      keys,
      errorMessages
    );
  };

  const validateUser = (
    keys: Array<string>, values: any
  ) => {
    return new UpdateUserManageable(values).validate(
      keys,
      errorMessages
    );
  };

  const confirmExperienceStep = {
    className: "investment-step",
    nextButtonText: () => t("Confirm"),
    nextStep: ({ values }: SubformProps<ValuesType>) =>
      isUserFitForSale(
        values[DataType.User],
        sale,
        values[DataType.Investment]
      )
        ? Step.CostCalculation
        : Step.ExperienceAdequacyMessage,
    onValidate: (values: any) =>
      validateUser(
        ["experience"],
        values[DataType.User]
      ),
    subform: ({
      errors, setFieldValue, touched, values
    }: SubformProps<ValuesType>) =>
      <UserExperienceSubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.User,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.User,
          new UpdateUserManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.User] || {}}
        values={values[DataType.User]}
      />,
    title: () => t("Confirm experience"),
  };

  const experienceAdequacyMessageStep = {
    className: "investment-step--wide",
    dotNumber: 1,
    nextButtonText: () => t("I understand and still want to invest"),
    nextStep: () => Step.CostCalculation,
    subform: ({ values }: SubformProps<ValuesType>) =>
      <UserExperienceAdequacyMessageSubform
        view={
          values[DataType.User].isExperienceProvided()
            ? UserExperienceAdequacyMessage.NotAdequate
            : UserExperienceAdequacyMessage.AdequacyEvaluationNotPossible
        }
      />,
    title: ({ values }: SubformProps<ValuesType>) =>
      values[DataType.User].isExperienceProvided()
        ? t("Inadequacy confirmation")
        : t("Check of adequacy not possible"),
  };

  const goingBackCheckStep = {
    backButtonText: () => t("Yes, take me back"),
    backStep: () => Step.ConfirmExperience,
    className: "investment-step--wide",
    dotNumber: 2,
    nextButtonText: () => t("No, I wish to continue"),
    subform: () => <GoingBackCheckSubform />,
    title: () => t("Are you sure you want to go back?"),
  };

  const chooseAmountStep = {
    className: "investment-step",
    onBackButton: () => {
      history.goBack();
    },
    onNextButton: (values: any) => {
      storeCurrentInvestment(values[DataType.Investment]);
    },
    onValidate: (values: any) => {
      let errors = validateInvestment(
        [
          "amountShares",
          "selfDisclosedMonthlyNetSalary",
          "selfDisclosedAvailableCash",
          "investingForThemselves",
        ],
        values[DataType.Investment]
      );

      const investment = new CreateInvestmentManageable(values[DataType.Investment]);
      const totalInvestmentAmount =
        currentInvestmentAmount + investment.amountShares;
      const isSelfDisclosureNeeded = () =>
        sale &&
        sale.isSelfDisclosureRequired() &&
        totalInvestmentAmount >
          InvestmentManageable.getSelfDisclosureInfo()
            .availableCashDisclosureLimit;
      const isSelfDisclosureValid = () =>
        investment.selfDisclosedMonthlyNetSalary ||
        totalInvestmentAmount <=
          InvestmentManageable.getSelfDisclosureInfo()
            .monthlyNetSalaryDisclosureLimit &&
          investment.selfDisclosedAvailableCash;
      const error = isSelfDisclosureNeeded() && !isSelfDisclosureValid();
      if (error) {
        errors.selfDisclosedMonthlyNetSalary = error;
        errors.selfDisclosedAvailableCash = error;
      }

      return errors;
    },
    subform: ({
      errors,
      setFieldValue,
      touched,
      values,
    }: SubformProps<ValuesType>) =>
      <ChooseAmountSubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.Investment,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.Investment,
          new CreateInvestmentManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.Investment] || {}}
        values={values[DataType.Investment]}
        sale={sale}
        currentInvestmentAmount={currentInvestmentAmount}
      />,
    subtitle: () => t("Choose amount"),
    title: () => t("Your investment in"),
  };

  const costCalculationStep = {
    backStep: () => Step.GoingBackCheck,
    className: "investment-step--wide",
    dotNumber: 2,
    nextButtonText: () => t("Confirm"),
    subform: ({
      errors,
      setFieldValue,
      touched,
      values,
    }: SubformProps<ValuesType>) =>
      <CostCalculationSubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.Investment,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.Investment,
          new CreateInvestmentManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.Investment] || {}}
        values={values[DataType.Investment]}
        sale={sale}
      />,
    title: () => t("Costs and Contributions"),
  };

  const walletStep = {
    className: "investment-step",
    dotNumber: 3,
    nextButtonText: () => t("Confirm"),
    nextStep: () =>
      storageUserDetails.wallet
        ? Step.PaymentTypeSelection
        : Step.WalletSuccessfullyCreated,
    nextStepOnNextButton: false,
    onNextButton: (values: any) => {
      const { walletPassword } = values[DataType.User];
      const { wallet } = storageUserDetails;

      const demonstratePrivateKeyDecryption = async (password: string) => {
        const encryptedPrivateKey = storageUserDetails.wallet
          ? storageUserDetails.wallet.encryptedPrivateKey
          : "";
        await decrypt(
          encryptedPrivateKey,
          password
        );
      };

      const goToNextStep = () => {
        const stepDescriptor = stepDescriptors.get(step) as StepDescriptor;
        setStep(stepDescriptor.nextStep());
      };

      if (wallet) {
        demonstratePrivateKeyDecryption(walletPassword).then(
          () => {
            goToNextStep();
          },
          () => {
            setError(t("Incorrect wallet password"));
          }
        );
      } else {
        authenticate({
          variables: {
            input: new LoginUserManageable({
              ...values[DataType.User],
              email: storageUserDetails.email,
            }).serialize(),
          },
          onSuccess: async () => {
            const randomWallet = ethers.Wallet.createRandom();
            const encryptedPrivateKey = await encrypt(
              randomWallet.privateKey,
              walletPassword
            );

            updateUser({
              variables: {
                input: new UpdateUserManageable({
                  wallet: {
                    publicKey: randomWallet.address,
                    encryptedPrivateKey,
                  } as CreateWalletInput,
                }).serialize(
                  ["wallet"],
                  storageUserDetails
                ),
              },
              onSuccess: () => {
                goToNextStep();
              },
            });
          },
        });
      }
    },
    onValidate: (values: any) =>
      validateUser(
        ["walletPassword"].concat(storageUserDetails.wallet ? [] : ["confirmWalletPassword", "password"]),
        values[DataType.User]
      ),
    subform: ({
      errors, setFieldValue, touched, values
    }: SubformProps<ValuesType>) =>
      <WalletSubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.User,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.User,
          new UpdateUserManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.User] || {}}
        values={values[DataType.User]}
        user={storageUserDetails}
      />,
    title: () =>
      storageUserDetails.wallet
        ? t("Connect wallet")
        : t("Setup your token wallet"),
  };

  const walletSuccessfullyCreatedStep = {
    backStep: () => Step.CostCalculation,
    className: "investment-step",
    dotNumber: 3,
    subform: () =>
      <WalletSuccessfullyCreatedSubform user={storageUserDetails} />
    ,
  };

  const paymentTypeSelectionStep = {
    backStep: () => Step.Wallet,
    className: "investment-step--narrow",
    dotNumber: 4,
    nextButtonText: () => t("Complete purchase"),
    onBackButton: (values: any) => {
      storeCurrentInvestment(values[DataType.Investment]);
    },
    onNextButton: (values: any) => {
      storeCurrentInvestment(values[DataType.Investment]);
    },
    onValidate: (values: any) =>
      validateInvestment(
        ["paymentMethodType"],
        values[DataType.Investment]
      ),
    subform: ({
      errors, setFieldValue, touched, values
    }: SubformProps<ValuesType>) =>
      <PaymentTypeSelectionSubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.Investment,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.Investment,
          new CreateInvestmentManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.Investment] || {}}
        values={values[DataType.Investment]}
      />,
    subtitle: () => t("Payment type selection"),
    title: () => t("Your investment in") + ` ${sale?.title || ""}`,
  };

  const summaryStep = {
    className: "investment-step--narrow",
    dotNumber: 5,
    nextButtonText: () => t("Complete purchase"),
    nextStepOnNextButton: false,
    onBackButton: (values: any) => {
      storeCurrentInvestment(values[DataType.Investment]);
    },
    onNextButton: (values: any) => {
      storeCurrentInvestment(values[DataType.Investment]);

      const input = new UpdateUserManageable(values[DataType.User]).serialize(
        ["experience"],
        storageUserDetails
      );

      if (isEmptyObject(input)) {
        createInvestment({ variables: { input: new CreateInvestmentManageable(values[DataType.Investment]).serialize() } });
      } else {
        updateUser({
          variables: { input },
          onSuccess: () => {
            createInvestment({ variables: { input: new CreateInvestmentManageable(values[DataType.Investment]).serialize() } });
          },
        });
      }
    },
    onValidate: (values: any) =>
      validateInvestment(
        ["documents"],
        values[DataType.Investment]
      ),
    subtitle: () => t("Summary"),
    subform: ({
      errors,
      setFieldValue,
      touched,
      values,
    }: SubformProps<ValuesType>) =>
      <SummarySubform
        setFieldValue={(
          id: string, value: any
        ) =>
          setFieldWrapper(
            id,
            value,
            values,
            DataType.Investment,
            setFieldValue
          )
        }
        setValues={(values: any) => setFieldValue(
          DataType.Investment,
          new CreateInvestmentManageable(values)
        )}
        errors={errors}
        touched={touched[DataType.Investment] || {}}
        values={values[DataType.Investment]}
        sale={sale}
      />,
    title: () => t("Your investment in") + ` ${sale?.title || ""}`,
  };

  const stepDescriptors = new Map<number, Partial<StepDescriptor>>([
    [Step.ConfirmExperience, confirmExperienceStep],
    [Step.ExperienceAdequacyMessage, experienceAdequacyMessageStep],
    [Step.ChooseAmount, chooseAmountStep],
    [Step.GoingBackCheck, goingBackCheckStep],
    [Step.CostCalculation, costCalculationStep],
    [Step.Wallet, walletStep],
    [Step.WalletSuccessfullyCreated, walletSuccessfullyCreatedStep],
    [Step.PaymentTypeSelection, paymentTypeSelectionStep],
    [Step.Summary, summaryStep],
  ]);

  useCustomQuery(GetCurrentUserInvestments({}));

  // Options
  const initialValues = {
    [DataType.Investment]: new CreateInvestmentManageable(sale
      ? {
        saleId: sale.id,
        amountShares: sale.minPurchase,
        minAmount: sale.minPurchase,
        maxAmount: Math.min(
          sale.maxPurchase || Infinity,
          sale.sharesAvailable,
          sale.isSelfDisclosureRequired()
            ? InvestmentManageable.getMaxUserInvestmentPerSale() -
                    currentInvestmentAmount
            : Infinity
        ),
        paymentMethodId: storageUserDetails.defaultPaymentMethod || undefined,
        documents:
              sale?.documents?.map((document: File) => ({
                name: document.id,
                valid: false,
              })) || [],
      }
      : null),
    [DataType.User]: new UpdateUserManageable(storageUserDetails),
  };

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        {sale &&
          <Flow
            error={error}
            maxNumberOfSteps={Step.Count}
            numberOfSteps={Step.Count - 2}
            initialValues={initialValues}
            step={step}
            setError={setError}
            setStep={setStep}
            stepsDescriptors={stepDescriptors}
            submitInProgress={
              loadingUpdateUser ||
              loadingCreateInvestment ||
              loadingAuthenticate
            }
          />
        }
      </ContentWrapper>
    </Box>
  );
};
